/**
 * i18n core config for the (first-pass) homepage + header + footer scope.
 *
 * English stays unprefixed at `/`. Arabic and Sorani live under `/ar` and
 * `/ckb`. Everything else on the site is English-only for now, so any href
 * that is not one of the translated routes is left unchanged.
 */

export const locales = ['en', 'ar', 'ckb'] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'en';

export const rtlLocales: Locale[] = ['ar', 'ckb'];

export function isRtl(locale: Locale): boolean {
  return rtlLocales.includes(locale);
}

/** Routes that currently have a translated version, relative to the site
 * root, without a trailing slash (the home route is represented as ''). Add
 * to this list as more pages get translated. */
const TRANSLATED_ROUTES = [
  '',
  '/careers',
  '/contact',
  '/about-datacode',
  '/portfolio',
  '/services',
  '/products',
  '/products/dilacrm',
  '/products/dilaerp',
  '/products/dilamenu',
  '/apply',
  '/support',
];

/** Route prefixes with a translated dynamic child (e.g. `/careers/<slug>`). */
const TRANSLATED_ROUTE_PREFIXES = ['/careers/'];

function isTranslatedRoute(route: string): boolean {
  return (
    TRANSLATED_ROUTES.includes(route) ||
    TRANSLATED_ROUTE_PREFIXES.some((prefix) => route.startsWith(prefix))
  );
}

/** Strips a leading locale segment (`/ar` or `/ckb`) from a pathname and
 * returns the remainder (still leading-slash-free-safe, e.g. '' for home). */
function stripLocalePrefix(pathname: string): string {
  const match = pathname.match(/^\/(ar|ckb)(\/.*)?$/);
  if (!match) return pathname === '/' ? '' : pathname.replace(/\/$/, '');
  const rest = match[2] ?? '';
  return rest.replace(/\/$/, '');
}

/** Detects the active locale from a pathname such as `/ar`, `/ar/foo`, `/ckb`
 * or anything else (which is treated as English). */
export function localeFromPathname(pathname: string): Locale {
  if (pathname === '/ar' || pathname.startsWith('/ar/')) return 'ar';
  if (pathname === '/ckb' || pathname.startsWith('/ckb/')) return 'ckb';
  return 'en';
}

/**
 * Builds the localized href for an internal link. Only routes that are
 * actually translated (currently just the homepage) get a locale prefix;
 * every other href is returned unchanged so it always points at the English
 * page (there is no Arabic/Sorani version of it yet).
 */
export function localizedHref(locale: Locale, href: string): string {
  // Leave external/hash/mailto/tel links alone.
  if (/^(https?:)?\/\//.test(href) || /^(mailto:|tel:|#)/.test(href)) {
    return href;
  }

  const withoutQuery = href.split('#')[0].split('?')[0];
  const route = withoutQuery === '/' ? '' : withoutQuery.replace(/\/$/, '');

  if (!isTranslatedRoute(route)) {
    return href;
  }

  if (locale === defaultLocale) {
    return href;
  }

  // route is '' (home) for now: every translated route maps to `/xx`.
  const suffix = href.slice(withoutQuery.length); // preserves #hash / ?query
  return `/${locale}${route}${suffix}` || `/${locale}`;
}

/**
 * Maps the current pathname to its equivalent under `target`. Used by the
 * language switcher. Any page that is not (yet) translated maps to the
 * target locale's home page, since there is nowhere else in-locale to send
 * the visitor.
 */
export function switchLocalePath(pathname: string, target: Locale): string {
  const route = stripLocalePrefix(pathname);

  if (!isTranslatedRoute(route)) {
    return target === defaultLocale ? '/' : `/${target}`;
  }

  if (target === defaultLocale) {
    return route === '' ? '/' : route;
  }

  return route === '' ? `/${target}` : `/${target}${route}`;
}
